/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Config } from '../../Config';
import Loading from '../../Components/base/loading/loading';
import { toastError } from '../../Components/toast';
import { Verified } from '../../models/user.model';
import { setIsSkipping } from '../../Services/navigationReducer';
import { useGetSettingValue } from '../../Services/settingReducer';
import { userApi } from '../../Services/userApi';
import { getCurrentUser } from '../../Services/userReducer';
import { useAppDispatch, useAppSelector } from '../../Store/hooks';
import InitialPage from '../InitialPage';
import BeemgPrimaryLogo from '../../Assets/NewIcons/beemg-logos-primary.svg';
import { Button } from '../../Components';

/**
 * A component for handling face verification.
 *
 * This component provides a responsive UI for users to initiate face verification
 * by clicking a button that opens the verification URL in the same window.
 * It also includes an option to resend the verification code positioned below the verify button.
 *
 * @returns {JSX.Element} The FaceVerify component.
 */
function FaceVerify() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(getCurrentUser);
  const faceVerificationURL = useGetSettingValue('FACE_VERIFICATION_LINK');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Opens the face verification URL in the same window with user parameters.
   */
  const handleVerifyFace = () => {
    const params: { [key: string]: string } = {
      cUserId: currentUser.id,
      cIPAddress: currentUser.ipAddress,
      cLanguage: i18n.language,
      apiUrl: Config.API_URL,
    };

    window.open(
      `${faceVerificationURL}faceverify?${new URLSearchParams(params).toString()}`,
      '_self'
    );
  };

  /**
   * Handles skipping face verification by updating user data.
   */
  const handleSkip = async () => {
    if (isLoading || !currentUser) return;
    setIsLoading(true);
    try {
      dispatch(setIsSkipping(true));
      const updateResult = await dispatch(
        userApi.endpoints.updateUser.initiate({
          ...currentUser,
          faceVerified: Verified.Skipped,
          verified: Verified.Complete,
        })
      ).unwrap();

      await new Promise(resolve => setTimeout(resolve, 300));
      const userDetails = await dispatch(
        userApi.endpoints.getCurrentUserDetails.initiate(undefined, { forceRefetch: true })
      ).unwrap();

      if (!userDetails || userDetails.faceVerified !== Verified.Skipped) {
        throw new Error('Failed to fetch updated user details');
      }

      navigate('/intro/choosePreference', { replace: true });
    } catch (error) {
      toastError(t('Failed to skip verification! Try Again Later'));
    } finally {
      setTimeout(() => {
        dispatch(setIsSkipping(false));
      }, 100);
      setIsLoading(false);
    }
  };

  return (
    <InitialPage>
      {isLoading ? (
        <div className="flex min-h-screen items-center justify-center">
          <Loading />
        </div>
      ) : (
        <div className="flex min-h-screen items-center justify-center p-4 sm:p-6">
          <div className="flex w-full max-w-md flex-col items-center rounded-lg bg-white p-6 sm:p-8">
            <img
              src={BeemgPrimaryLogo}
              alt="Beemg Logo"
              className="h-24 w-24 sm:h-28 sm:w-28 mb-2"
            />
            <span className="text-4xl sm:text-normal uppercase font-bold mb-4">
              {t('signUp')}
            </span>
            <h1 className="text-lg sm:text-normal font-semibold mb-4">
              {t('faceVerification')}
            </h1>
            <div className="w-full flex flex-col items-end">
              <Button
                id="btn_verifyFace"
                data-testid="btn_verifyFace"
                onClick={handleVerifyFace}
                className="w-64 max-w-[12rem] sm:max-w-[16rem] py-2 text-sm sm:text-base mb-2"
                disabled={isLoading}
              >
                {t('verifyYourFace')}
              </Button>
              <span
                className="text-BeeMG-red px-2 text-sm sm:text-base cursor-pointer hover:underline"
                id="btn_skip"
                onClick={handleSkip}
              >
                {t('skip')}?
              </span>
            </div>
          </div>
        </div>
      )}
    </InitialPage>
  );
}

export default FaceVerify;